import { ProblemsI } from './components/requestModal/RequestModal';
import { ModalBaseProps } from './components/calendar/CalendarPage';
import { EmployeeEventWithNewState } from './components/planner/ItemRow';
import { TemplateType } from './components/requestModal/types';
import { ReportOrgFilters } from './components/reports/ReportDetailOrg';

export type AuthType = {
  login: string,
  password: string
}

export interface UserList{
  countryName: UnionLocations
  id: number
  lastNameAndFirstNameWithSpace: string
  positionName: string
}

export type WhoisoffVersionType = 'NONE' | 'VERSION_1' | 'VERSION_2' | 'VERSION_3';

export interface UserDataI{
  id: number,
  positionName: string,
  name: string,
  portraitUrl: string,
  countryCode: UnionLocations,
  whoIsOffView: WhoisoffVersionType,
  globalSearchView: 'NONE' | 'VERSION_1' | 'VERSION_2',
  isAllUserRequiredDataPresent: boolean,
  isNewlistPopupViewed: boolean,
  ignoreCalendarValidations: boolean,
  hierarchyPath: string
}


export type LeaveTypeBase = {
  type: string,
  infoMessageVisible: boolean,
  requestName: string,
  requestTypeId: number,
  tooltip: string,
}


export type LeaveTypesI = LeaveTypeBase & {
  requestSubTypes?: LeaveTypeBase[];
}

export interface LeaveRequestPrerequisites {
  requestTypes: LeaveTypesI[],
  availableDays: {
    available: number,
    transfer: number,
  },
  leaveRequestCreationCalendarAccessDto: {
    availableStartDate: string | null,
    availableEndDate: string | null,
  }
}

export interface VacationTypesIds {
  vacation: number,
  vacationOnDemand: number,
}

export type SelectableDivisionsListItem = {
  hierarchyId: number,
  hierarchyName: string,
  hierarchyPath: string
}
export interface SelectableDivisions{
  perPage: number
  list: SelectableDivisionsListItem[],
  selectedItem: SelectableDivisionsListItem
}

export type DivisionPersentage =  {
  divisionId: number,
  divisionName: string,
  divisionPercentage: number,
  divisionPercentageRightForCurrentMonth: boolean
}

export type DataGridListItem = {
  activeEmployee: boolean
  division: string
  divisionId: number
  divisionPath: string
  id: number
  latinName: string
  latinSurname: string
  availableDays: number
  transferedDays: number
  location: string
  nationalName: null
  nationalSurname: null
  allowanceDays: number
}

export interface DataGridI {
  list: DataGridListItem[] ,
  onPage: number,
  page: number,
  perPage: number,
  total: number,
  firstNameAndLastNameOfInvalidUsers: string[]
}

export interface GeneralDivision{
  fetched: boolean
  dataGrid: DataGridI,
  header: {
    departmentPercentages: DivisionPersentage[],
    generalPercentage: number
  }
}

export interface SpecialDay{
  dayType: CalendarEventType
  description: string
  endDate: Date
  startDate: Date
  title: string,
  id: number,
  isWeekend?: boolean,
}

export interface SelectedDays {
  from: Date,
  to: Date,
  title: string,
  description: string,
  type: CalendarEventType,
  id: number,
  isWeekend?: boolean,
}

export type PickByValue<T, ValueType> = Pick<T, { [Key in keyof T]-?: T[Key] extends ValueType ? Key : never }[keyof T]>


export type LeaveRequestStatus = 'REJECTED' | 'PENDING' | 'CANCELLED' | 'APPROVED' | 'PLANNED'
export interface LeavesType{
  isPossibleToEdit: boolean
  comment: string,
  duration: number,
  isPossibleToCancelForEmployee: boolean,
  isPossibleToEditForEmployee: boolean,
  editedForEmployeeAt: string | null
  editedForEmployeeBy: string | null
  editedForEmployeeReason: string | null
  editorFullName: string | null
  endDate: string,
  fileRequisites: [
    {
      description: string,
      id: number,
      name: string,
      ref:string
    }
  ],
  id: number,
  startDate: string,
  status: LeaveRequestStatus,
  type: string,
  userId: number,
  isPostfactum: boolean,
  requestName: string,
  isPossibleToCancel: boolean
}

export interface LeavesAll{
  leaveRequests: LeavesType[],
  periodDuration: string,
  id: number,
  periodType:string,
  isCurrentPeriod: boolean,
  isPeriodBeforeRelocation: boolean
}

export interface LeaveRequestId{
  availableDaysForNow: number,
  isPossibleToCancelForEmployee: boolean,
  cancellerFullName: string,
  cancelledForEmployeeReason: string,
  isPossibleToEditForEmployee: boolean,
  editedForEmployeeAt: string | null
  editedForEmployeeBy: string | null
  editedForEmployeeReason: string | null
  editorFullName: string | null
  approvals: [
    {
      comment: string,
      date: string,
      id: number,
      leaveRequestId: number,
      name: string,
      portraitUrl: string,
      role: string,
      status: string,
      userId: number
    }
  ],
  approver: {
    email: string,
    id: number,
    name: string,
    portraitUrl: string
  },
  calendarDuration: number,
  comment: string,
  createdAt: string,
  createdBy: string,
  endDate: string,
  requestTypeCountryName: UnionLocations,
  fileRequisites: [
    {
      description: string,
      id: number,
      name: string,
      ref: string
    }
  ],
  id: number,
  requestName: string,
  requestTypeId: number,
  requester: {
    country: UnionLocations | ''
    email: string,
    id: number,
    name: string,
    portraitUrl: string,
    positionName: string,
  },
  startDate: string,
  status: LeaveRequestStatus,
  type: string,
  updatedAt: string,
  userId: number,
  workdaysAmount: number,
  isPostfactum: boolean,
  warningMessages:ProblemsI[],
  viewMode: 'VIEW' | 'EDIT' | 'APPROVE' | '',
  isPossibleToApproveOrReject: boolean,
  isPossibleToCancel: boolean,
  cancellationReason: string,
  availableDays: number,
  isPossibleToEdit: boolean,
  periodType: UnionPeriodType,
  periodString: string,
}

export type MembersDataListType = {
  userActive: boolean
  allowanceDays: number
  casualLeaveDays: number
  columns: Record<number, string>
  dayOffDays: number
  extraLeaveDays: number
  id: number
  latinName: string
  latinSurname: string
  location: string
  nationalName: string
  nationalSurname: string
  parentalLeaveDays: number
  availableDays: number
  sickLeaveDays: number
  totalLeaveDays: number
  unpaidLeaveDays: number
}

export interface MembersDataI{
  headDivision: SelectableDivisionsListItem,
  list: MembersDataListType[],
  totalDaysUsedPercentage: number
}


export interface NotificationI{
  id: number,
  endDate: string,
  message: string,
  messageTime: string,
  startDate: string,
  status: string,
  type: string,
  leaveRequestId: number,
  notificationType: string,
  duration: number,
  authorName: string
}

export interface MenuItemsI {
  id: number,
  alias: string,
  itemCode: string,
  name: string,
  ordinal: number,
  visible: boolean,
  disable: boolean
}

export interface AccessItemI{
  actionCodes: string[],
  description: string,
  label: string,
  name: string,
  refs: {
    route: string
  },
  isModificationAllowed?: boolean,
  isApproveAllowed?: boolean
}

export interface AccessTypeCode {
  type: 'MENUITEM' | 'ACCESSCODE',
  code: string,
}

export interface UserLocationI{
  location: {
    id: number,
    name: string
  },
  country: UnionLocations
}

export type LEAVE_TYPES = 'DAY_OFF' |
  'EXTRA_LEAVE' |
  'MILITARY_LEAVE' |
  'PARENTAL_LEAVE' |
  'SICK_LEAVE' |
  'UNEXCUSED_ABSENCE' |
  'UNPAID_LEAVE' |
  'VACATION';


export type CalendarEventType = 'HOLIDAY' | 'WORKING'

export type UnionPeriodType = 'START_YEAR' | 'START_DATE'

export type RemaniningDays = {
  usedInFuturePeriod: number,
  staticTranferDays: number,
  allowanceString: string,
  availableForNowWithoutTransfer: number,
  usedInThisPeriod: number,
  usedInTransferDays: number,
  userId:number,
  available:number,
  allowance:number,
  transfer:number,
  taken:number,
  annualBalance:number,
  availableForNow:number,
  countryName:UnionLocations | '',
  periodString:string,
  compensatoryOffBalance:number,
  periodType: UnionPeriodType,
  possibleToEditCBalance: boolean
  takencompensatoryOffBalance: number,
  isPossibleToEditEmployeeBalance: boolean,
  isPossibleToViewEmployeeBalance: boolean,
  employeeAllowanceBalance: number
  employeeTransferDaysBalance: number
}

export type RemaniningDaysForAnotherUser = Pick<RemaniningDays, 'available' | 'transfer'>

//help types
type Locales = 'EN' | 'RU';

//templates types
export type LeaveTypesStringType = 'Vacation' | 'Sick Leave' | 'Day off' | 'Occasional Leave' | 'Unpaid Leave' | 'Parental Leave' | 'Unexcused Absence' | 'Military Leave';

export type LeaveTypeBody = {
  id: null | number,
  name: string,
  description: null | string,
  ref: string
}

export type LeaveType  = Partial<Record<LeaveTypesStringType, LeaveTypeBody>>

export type LeaveTemplatesType = {
  leaveTemplates : LeaveType
}

export type LocaleBodyType =  Record<Locales, LeaveTemplatesType>

export type LocaleTemplateType = {
  localeTemplates: LocaleBodyType
}

//attachments types
type AttachmentBodyType = {
  attachmentType: string,
  name: string,
  ref: string
}

type AttachmentType = Array<AttachmentBodyType>

export type LocaleAttachmentsBodyType = Record<Locales, AttachmentType>

export type LocaleAttachmentsType = {
  localeTemplates: LocaleAttachmentsBodyType
}

export type ProjectReportRes = {
  leaveTypesForParticipantCountry: string[]
  dataGrid: {
    employeeDto: EmployeeEventsResponse,
    orderNumber: number,
    userActive: boolean,
    profileLinkVisible: boolean
  }[],
  workingDaysForProject: {
    countryCode: string
    stringValue: string
    totalDays: number
  }[]
}

export type UnionLocations =  'Czech Republic' | 'Belarus' | 'Kazakhstan' | 'Poland' | 'Bulgaria' | 'Ukraine' | 'Georgia' | 'Lithuania'


export interface EmployeeEvent{
  id: number | null,
  requestId: number | null,
  specialDayId: number | null,
  startDate: string | Date,
  endDate: string | Date,
  status: string | null,
  type: string | null,
  userId: number | null,
  countryCode: null | string,
  category: string | null,
  originalTypeName: string | null,
}

export interface EmployeesEvents {
  events: EmployeeEventWithNewState[],
  fullName: string,
  id: number,
  countryCode: string,
  portraitUrl: string,
  positionName: string,
  totalAbsence: TotalAbsenceCategory[],
  totalWorkedForParticipant: number,
  totalUsedForParticipant: number,
  totalForVacation: number
  isUserActive: boolean,
  isProfileLinkVisible: boolean
}

export type TotalAbsenceCategory = {
  leaveCategory: string,
  leaveCount: number
}

export interface EmployeeEventsResponse extends Omit<EmployeesEvents, 'events'>{
  events: EmployeeEvent[]
}

export interface EmployeesPlanerI {
  email: string
  firstNameAndLastName: string
  firstname: string
  firstnameNational: string
  fullFIO: string
  fullFIONational: string
  hierarchyId: number
  hierarchyName: string
  hierarchyPath: string
  id: number
  lastname: string
  lastnameNational: string
  location: string
  middlename: string
  middlenameNational: string
  name: string
  portraitUrl: string
  positionName: string
}


export type PlannerTypeUnion = 'DIVISION' | 'PROJECT' | 'CUSTOM' | 'CIRCLE'

export type PlannerListMap = {
  [T in  PlannerTypeUnion]: PlannerListItem[]
}


export interface PlannerListDTO {
  name: string,
  ownerId: number,
  userIds: number[]
  id: number,
  plannerListType: {
    editable: boolean,
    type: PlannerTypeUnion
  }
}


export interface PlannerListItem {
  name: string,
  ownerId: number,
  userIds: number[]
  id: number,
  plannerListType: {
    editable: boolean,
    type: PlannerTypeUnion
  }
}
export interface RequestModalProps{
  onClose: () => void,
  currentItem: Partial<RequestsI>,
  selectedYear?: number,
}

export interface CalendarModalProps{
  onClose: () => void,
  data: ModalBaseProps
}

export interface RequestsI {
  comment: string,
  duration: number,
  employeeName: string,
  employeePosition: string,
  endDate: string,
  isPossibleToCancelForEmployee?: boolean,
  fileRequisites: [
    {
      description: string,
      id: number,
      name: string,
      ref: string
    }
  ],
  id: number,
  portraitUrl: string,
  requestName: string,
  requestTypeId: number,
  startDate: string,
  status: string,
  userId: number,
  warningMessages: ProblemsI[],
  isPostfactum: boolean
}

export type LightRequest = Pick<RequestsI, 'id' | 'startDate' | 'endDate' | 'employeeName'>;

export interface ApproveRequestModalProps{
  onClose: () => void,
  currentItem: LightRequest,
  selectedYear?: number,
}

export interface AbsenceI {
  absentEmployeesNumber: number,
  leaveRequests:
    {
      categoryId: number,
      categoryName: string,
      comment: string,
      createdAt: string,
      duration: number,
      employeeName: string,
      employeePosition: string,
      endDate: string,
      fileRequisites: [
        {
          description: string,
          id: number,
          name: string,
          ref: string
        }
      ],
      id: number,
      isPostfactum: boolean,
      portraitUrl: string,
      requestName: string,
      requestTypeId: number,
      startDate: string,
      status: string,
      type: string,
      userId: number,
      warningMessages: [
        {
          level: string,
          message: string
        }
      ]
    }[]
  ,
  listName: string
}

export interface AbsenceListItemI{
  listName: string,
  listType: string,
  plannerListId: number,
  selected: boolean
}

// type AbsenceFilterDtoI = {
//   filterDto: AbsenceListItemI,
//   subFilterDtos: AbsenceFilterDtoI[],
// }

export interface AbsenceFilterDtoExtI extends AbsenceListItemI {
  childrenIds: number[],
}

export interface AbsenceFilterDtoI extends AbsenceListItemI {
  subFilterDtos: AbsenceFilterDtoI[],
}

export interface AbsenceSettingsUpdateBodyI {
  blockOfFilters: AbsenceListItemI[],
  showLastTenEmployees: boolean,
  displayAllNewListsFromMyListCategory: boolean,
  allCirclesListsChosen: boolean,
  allProjectListsChosen: boolean,
  allDivisionListsChosen: boolean,
  allPlannerListsChosen: boolean,
}

// type OrgReportDivisionItem = {
//   id: number;
//   name: string;
//   title: string;
//   value: number;
//   key: number;
//   parentId: number | null;
//   children: OrgReportDivisionItem[];
//   selected: boolean;
// }

export interface AbsenceListsForUserI {
  list: Array<AbsenceListItemI>,
}

export interface AbsenceSettingsI {
  filtersList: Array<AbsenceListItemI>,
  filtersDivisionsTree: AbsenceFilterDtoI,
  showLastTenEmployees: boolean,
  displayAllNewListsFromMyListCategory: boolean,
}

export type AbsenceListsT = {
  listName: string,
  listType: string,
  plannerListId?: number
}

type SpecialDaysReasonUnion = ': PUBLIC HOLIDAY'

export type AbsentEmployeesT = {
  absentEmployeesNumber: 0,
  specialDays? : {
    countryCode: string,
    employeeName: string,
    id: string,
    startDate: string,
    endDate: string,
    portraitUrl: string,
    employeePosition: string,
    reason: SpecialDaysReasonUnion
  }[]
  leaveRequests: [
    {
      cancellationReason: string,
      categoryId: number,
      categoryName: string,
      comment: string,
      countryCode: string,
      createdAt: string,
      duration: number,
      employeeName: string,
      employeePosition: string,
      endDate: string,
      fileRequisites: [
        {
          description: string,
          id: number,
          name: string,
          ref: string
        }
      ],
      id: number,
      isPossibleToCancel: boolean,
      isPostfactum: boolean,
      portraitUrl: string,
      requestName: string,
      requestTypeId: number,
      source: string,
      startDate: string,
      status: string,
      type: string,
      userApproveRequestDto: {
        countryCode: string,
        countryName: string,
        email: string,
        firstNameAndLastNameWithSpace: string,
        firstname: string,
        id: number,
        lastname: string,
        location: string,
        middlename: string
      },
      userId: number,
      warningMessages: [
        {
          level: string,
          message: string
        }
      ]
    }
  ],
  listName: string
}

export type FiltersPages = 'DEPARTMENT_REPORT' | 'ORGANIZATION_REPORT' | 'PROJECT_REPORT'

export type FilterElement = {
  element: string,
  value :string
}

export interface UserFilters {
  blockOfFilters:  FilterElement[],
  id: number,
  pageTypeSetting: FiltersPages
  userId: number
}

export type FiltersMapping = {
  [K in FiltersPages]: UserFilters
}

export enum TypePageDaysAvailable {
  HOME = 'HOME',
  PROFILE = 'PROFILE'
}

export type EditCompensatoryOff = {
  balanceValue: number,
  employeeId: number,
  comment?: string
}


export type SpecialDays = {
  endDay: string,
  holidayCount: number,
  locale: UnionLocations,
  startDate: string,
  workingDayCount: number,
  workingWeekendCount: number
}

export type ImageType = {
  originalUrl: string,
  blob: string
}

export type SystemState = {
  startDateTime: string,
  endDateTime: string,
  reason: string,
  isNecessaryToBlock: boolean,
  isNecessaryToShowMessage: boolean,
}


export type DownloadTemplate = {templateType?: TemplateType, requestTypeId: number, templateId: number, startDate?: string, endDate?: string, workDays?: number, calendarDays?: number, userId?: number}

export interface ActingRole{
  actingId: number,
  actingName: string,
  id: number | null,
  roleAlias: string,
  roleId: number,
  roleName: string,
  userId: number
}

enum IndexedFileStatus {
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS'
}
interface IndexedFile extends File {
  uuid: string;
  status: IndexedFileStatus;
  errorMessage?: string;
}
export interface FormFields {
  title: string;
  description: string;
  screenshots: IndexedFile[];
  attachments: IndexedFile[];
}

export type PlannerFilterDto = [
  {
    element: 'SELECTED_LIST_NAME',
    value: string
  },
  {
    element: 'LIST_TYPE',
    value: string | number
  },
  {
    element: 'ID',
    value: string | number
  },
]

export type ReportDepStateDto = [
  {
    element: 'SELECTED_DIVISION',
    value: string | null
  },
  {
    element: 'PAGE_SIZE',
    value: number | null
  },
]


export type OrgReportDivisionItem = {
  id: number,
  name: string,
  title: string,
  value: number
  key: number
  parentId: number | null,
  children: OrgReportDivisionItem[],
  selected: boolean
}

export type OrgReportLocationItem = {
  id: number | string,
  name: string,
  selected: boolean
}

export type OrgReportSettings = {
  allDivisionsSelected: boolean,
  divisionTreeForHrReportFilterDtos: OrgReportDivisionItem[],
  allLocationsSelected: boolean,
  locations: OrgReportLocationItem[],
  includeB2B: boolean,
  includeOnlyPendingAbsence: boolean
}

export type OrgReportSettingsUpdateDto = {
  isAllDivisionsSelected:boolean,
  divisionIds:number[],
  isAllLocationsSelected:boolean,
  locationIds:(string | number)[],
  includeB2B: boolean,
  includeOnlyPendingAbsence: boolean
}

export type OrgReportPayload = {
  dateFilter: string,
  divisionsAndLocationsFilter: OrgReportSettingsUpdateDto
}

export type HrReportDto = ReportOrgFilters & {
  divisionsAndLocationsFilter: OrgReportSettingsUpdateDto
}

export type HrReportCategory = {
  id: number,
  type: string,
  name: string,
}

export type HrReportEmployeeDto = {
  allowanceDays: number
  availableDays: number,
  totalUsed: number,
  countryCode: string,
  fullName: string,
  userActive: boolean,
  hasPendingRequests: boolean,
  id: number,
  orderNumber: number
  profileLinkVisible: boolean,
  countryCodeToolTip: string,
  totalAbsence:
    {leaveCategory: string, leaveCount: number}[]
}

export type HrReportResponse = {
  categories: HrReportCategory[],
  employeesDtoList:HrReportEmployeeDto[]
  total: number,
  firstNameAndLastNameOfInvalidUsers: string[],
}

export type CalendarTypeUnion = 'YEAR' | 'MONTH' | 'LIST'

export type CalendarSettingsType = {
  country: UnionLocations | undefined,
  month: string,
  year: number,
  calendarType: CalendarTypeUnion, 
}

interface FeatureSwitcherSelector {
  value: boolean,
  enabled: boolean,
  summaryValue: boolean,
}

export type FeatureSwitcherSelectorsType = Record<string, FeatureSwitcherSelector>;

export type SysAdminAPIsResponse = {
  allRequests: SysAdminAPIDto[],
}

export type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

export interface SysAdminAPIDto {
  title: string,
  description: string,
  method: Method,
  api: string,
  parametersDescription: string,
  port: number
}

export type ObligatoryType = 'OBLIGATORY' | 'NON_OBLIGATORY' | 'AUTOMATICALLY';